import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Palette, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { FillSettings } from '../../types/design';

interface UniversalColorPickerProps {
  value: FillSettings;
  onChange: (value: FillSettings, commit?: boolean) => void;
  label?: string;
  id?: string;
}

export const UniversalColorPicker: React.FC<UniversalColorPickerProps> = ({
  value,
  onChange,
  label = "Color",
  id = "universal-popover-portal"
}) => {
  const [activePopover, setActivePopover] = useState<'solid' | 'grad1' | 'grad2' | null>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  
  const triggerRefs = {
    solid: useRef<HTMLButtonElement>(null),
    grad1: useRef<HTMLButtonElement>(null),
    grad2: useRef<HTMLButtonElement>(null),
  };

  useLayoutEffect(() => {
    if (activePopover) {
      const trigger = triggerRefs[activePopover].current;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        setPopoverPos({
          top: rect.bottom + 8,
          left: Math.max(16, rect.left - 80)
        });
      }
    }
  }, [activePopover]);

  useEffect(() => {
    if (!activePopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      const portal = document.getElementById(id);
      const isTrigger = Object.values(triggerRefs).some(ref => ref.current?.contains(e.target as Node));
      if (portal && !portal.contains(e.target as Node) && !isTrigger) {
        setActivePopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopover, id]);

  const updateFill = (updates: Partial<FillSettings>, commit = false) => {
    onChange({ ...value, ...updates }, commit);
  };

  const renderColorGrid = (currentValue: string, onSelect: (color: string) => void) => (
    <div className="grid grid-cols-5 gap-2 mb-3">
      {['#000000', '#ffffff', '#f87171', '#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#f472b6', '#94a3b8'].map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onSelect(c)}
          className={`w-7 h-7 rounded-lg border border-gray-100 shadow-sm transition-transform hover:scale-110 active:scale-95 ${currentValue === c ? 'ring-2 ring-[#30A84B] ring-offset-1' : ''}`}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );

  const renderHexInput = (currentValue: string, onSelect: (color: string) => void) => (
    <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="relative w-6 h-6 rounded-lg overflow-hidden border border-gray-200 shadow-sm group">
          <input
            type="color"
            value={currentValue && currentValue !== 'transparent' ? currentValue : '#000000'}
            onChange={(e) => onSelect(e.target.value)}
            className="absolute inset-[-4px] w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer bg-transparent border-none p-0"
          />
        </div>
        <span className="text-[11px] font-mono font-bold text-gray-500 uppercase">
          {(currentValue && currentValue !== 'transparent' ? currentValue : '#000000').replace('#', '')}
        </span>
      </div>
      <Palette className="w-3.5 h-3.5 text-gray-400" />
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      {/* Type Toggle */}
      <div className="flex items-center gap-0.5 bg-gray-100/50 p-0.5 rounded-lg border border-gray-100">
        {(['solid', 'gradient'] as const).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => updateFill({ type })}
            className={`px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-wider transition-all ${value.type === type
              ? 'bg-white text-[#30A84B] shadow-sm'
              : 'text-gray-400 hover:text-gray-600'
              }`}
          >
            {type}
          </button>
        ))}
      </div>

      {value.type === 'solid' ? (
        <div className="relative flex items-center gap-2">
          <button
            ref={triggerRefs.solid}
            type="button"
            onClick={() => setActivePopover(activePopover === 'solid' ? null : 'solid')}
            className="flex items-center gap-1.5 bg-gray-50/50 p-1 rounded-full border border-gray-100 hover:bg-gray-100 transition-colors"
          >
            <div
              className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
              style={{
                backgroundColor: value.color && value.color !== 'transparent' ? value.color : 'transparent',
                backgroundImage: value.color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%, #ccc)' : 'none',
                backgroundSize: '4px 4px',
              }}
            >
              {value.color === 'transparent' && <div className="w-full h-0.5 bg-red-500/80 rotate-45" />}
            </div>
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${activePopover === 'solid' ? 'rotate-180' : ''}`} />
          </button>

          {activePopover === 'solid' && createPortal(
            <AnimatePresence>
              <motion.div
                id={id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="fixed w-[200px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-xl z-[10000] p-3 overflow-hidden"
                style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{label}</div>
                {renderColorGrid(value.color, (color) => {
                    updateFill({ color, enabled: true }, true);
                    setActivePopover(null);
                })}
                {renderHexInput(value.color, (color) => updateFill({ color, enabled: true }, true))}
              </motion.div>
            </AnimatePresence>,
            document.body
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {/* Grad Start */}
          <div className="relative">
            <button
              ref={triggerRefs.grad1}
              type="button"
              onClick={() => setActivePopover(activePopover === 'grad1' ? null : 'grad1')}
              className="flex items-center gap-1.5 bg-gray-50/50 p-1 rounded-full border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <div
                className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: value.gradientStart || '#000000' }}
              />
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${activePopover === 'grad1' ? 'rotate-180' : ''}`} />
            </button>
            {activePopover === 'grad1' && createPortal(
              <AnimatePresence>
                <motion.div
                  id={id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="fixed w-[200px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-xl z-[10000] p-3 overflow-hidden"
                  style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Start Color</div>
                  {renderColorGrid(value.gradientStart || '#000000', (c) => {
                      updateFill({ gradientStart: c }, true);
                      setActivePopover(null);
                  })}
                  {renderHexInput(value.gradientStart || '#000000', (c) => updateFill({ gradientStart: c }, true))}
                </motion.div>
              </AnimatePresence>,
              document.body
            )}
          </div>

          {/* Grad End */}
          <div className="relative">
            <button
              ref={triggerRefs.grad2}
              type="button"
              onClick={() => setActivePopover(activePopover === 'grad2' ? null : 'grad2')}
              className="flex items-center gap-1.5 bg-gray-50/50 p-1 rounded-full border border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <div
                className="w-5 h-5 rounded-full border-2 border-white shadow-sm flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: value.gradientEnd || '#ffffff' }}
              />
              <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${activePopover === 'grad2' ? 'rotate-180' : ''}`} />
            </button>
            {activePopover === 'grad2' && createPortal(
              <AnimatePresence>
                <motion.div
                  id={id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="fixed w-[200px] bg-white border border-gray-200 shadow-[0_12px_40px_rgba(0,0,0,0.12)] rounded-xl z-[10000] p-3 overflow-hidden"
                  style={{ top: `${popoverPos.top}px`, left: `${popoverPos.left}px` }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">End Color</div>
                  {renderColorGrid(value.gradientEnd || '#ffffff', (c) => {
                      updateFill({ gradientEnd: c }, true);
                      setActivePopover(null);
                  })}
                  {renderHexInput(value.gradientEnd || '#ffffff', (c) => updateFill({ gradientEnd: c }, true))}
                </motion.div>
              </AnimatePresence>,
              document.body
            )}
          </div>

          {/* Gradient Angle Input */}
          <div className="flex items-center gap-1.5 ml-1">
             <input
                type="number"
                value={value.gradientAngle || 0}
                onChange={(e) => updateFill({ gradientAngle: parseInt(e.target.value) || 0 }, true)}
                className="w-8 bg-gray-50 border border-gray-100 rounded text-[10px] font-bold text-gray-600 p-0.5 text-center outline-none focus:border-[#30A84B]"
                title="Gradient Angle"
             />
             <span className="text-[9px] font-bold text-gray-400 tracking-tighter">°</span>
          </div>
        </div>
      )}
    </div>
  );
};
